import React, { useState, useEffect, useMemo } from 'react';

const SelectRouteReport = ({ fetchReports, customers, customerDeliveries = [] }) => {
    const [mode, setMode] = useState('legacy');
    const [customer, setCustomer] = useState('');
    const [route, setRoute] = useState('');
    const [customersList, setCustomersList] = useState([]);
    const [routesList, setRoutesList] = useState([]);
    const [selectedCustomerId, setSelectedCustomerId] = useState('');
    const [selectedDeliveryId, setSelectedDeliveryId] = useState('');
    const [error, setError] = useState('');

    useEffect(() => {
        if (!Array.isArray(customers) || customers.length === 0) return;
        const withRoute = customers.filter(c => typeof c.route === 'string' && c.route.trim() !== '');
        const names = [...new Set(withRoute.map(c => c.company))].sort((a, b) => a.localeCompare(b));
        setCustomersList(names);
    }, [customers]);

    useEffect(() => {
        setRoute('');
        if (!customer) { setRoutesList([]); return; }
        const routes = [...new Set(
            customers.filter(c => c.company === customer && typeof c.route === 'string' && c.route.trim())
                     .map(c => c.route.trim())
        )].sort((a, b) => a.localeCompare(b));
        setRoutesList(routes);
    }, [customer, customers]);

    const newCompanyList = useMemo(() => {
        const seen = new Map();
        (customerDeliveries || []).forEach(d => {
            if (d.customer_id && !seen.has(d.customer_id)) {
                seen.set(d.customer_id, d.customer_company || d.company_name || ('ID ' + d.customer_id));
            }
        });
        return [...seen.entries()]
            .map(([id, name]) => ({ id, name }))
            .sort((a, b) => a.name.localeCompare(b.name));
    }, [customerDeliveries]);

    const deliveryPointList = useMemo(() => {
        if (!selectedCustomerId) return [];
        return (customerDeliveries || [])
            .filter(d => String(d.customer_id) === String(selectedCustomerId))
            .sort((a, b) => (a.route_name || '').localeCompare(b.route_name || ''));
    }, [selectedCustomerId, customerDeliveries]);

    const handleSubmit = () => {
        setError('');
        if (mode === 'new') {
            if (!selectedCustomerId) { setError('Please select a customer.'); return; }
            fetchReports({
                mode: 'new',
                customer_id: selectedCustomerId,
                customer_deliveries_id: selectedDeliveryId || undefined,
            });
        } else {
            if (!customer) { setError('Please select a customer.'); return; }
            fetchReports({ mode: 'legacy', customer, route: route || '' });
        }
    };

    const selectStyle = { padding: '5px', textAlign: 'center', width: '100%', maxWidth: 340 };
    const btnStyle = { padding: '8px 10px', width: '100%', maxWidth: 340, backgroundColor: '#007bff', color: '#fff', border: 'none', borderRadius: 5, cursor: 'pointer' };
    const modeBtn = (m) => ({
        padding: '6px 18px', border: 'none', borderRadius: 5, cursor: 'pointer', fontWeight: mode === m ? 700 : 400,
        background: mode === m ? '#007bff' : '#e0e0e0', color: mode === m ? '#fff' : '#333',
    });

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, justifyContent: 'center', alignItems: 'center', margin: '20px auto', padding: '40px', width: '100%', border: '2px solid', borderRadius: 10, minHeight: 290 }}>
            <h2>Route Reports</h2>

            <div style={{ display: 'flex', gap: 8, marginBottom: 4 }}>
                <button style={modeBtn('legacy')} onClick={() => { setMode('legacy'); setError(''); }}>Legacy Search</button>
                <button style={modeBtn('new')} onClick={() => { setMode('new'); setError(''); }}>New System</button>
            </div>

            {mode === 'legacy' ? (
                <>
                    <small style={{ opacity: 0.6, marginBottom: 4 }}>Search all historical routes by company name snapshot</small>
                    <select value={customer} onChange={e => setCustomer(e.target.value)} style={selectStyle}>
                        <option value="">Select Customer</option>
                        {customersList.map((c, i) => <option key={i} value={c}>{c}</option>)}
                    </select>
                    <select value={route} onChange={e => setRoute(e.target.value)} style={selectStyle}>
                        <option value="">All Routes</option>
                        {routesList.map((r, i) => <option key={i} value={r}>{r}</option>)}
                    </select>
                </>
            ) : (
                <>
                    <small style={{ opacity: 0.6, marginBottom: 4 }}>Search by customer account &mdash; shows routes linked via customer_id</small>
                    <select value={selectedCustomerId} onChange={e => { setSelectedCustomerId(e.target.value); setSelectedDeliveryId(''); }} style={selectStyle}>
                        <option value="">Select Customer</option>
                        {newCompanyList.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                    {selectedCustomerId && (
                        <select value={selectedDeliveryId} onChange={e => setSelectedDeliveryId(e.target.value)} style={selectStyle}>
                            <option value="">All Delivery Points</option>
                            {deliveryPointList.map(d => (
                                <option key={d.id} value={d.id}>
                                    {d.company_name || d.customer_company} &mdash; {d.route_name || d.address || ''}
                                </option>
                            ))}
                        </select>
                    )}
                </>
            )}

            <button className="hoverable" onClick={handleSubmit} style={btnStyle}>Search</button>
            {error && <p style={{ color: 'red', marginTop: 10 }}>{error}</p>}
        </div>
    );
};

export default SelectRouteReport;
